import React, { useEffect, useState } from "react";
import {
  CreateProfileData,
  ProfileContent,
  profileService,
  UpdateProfileData,
} from "../../../services/Tentang/TentangService";
import Sidebar from "../../../component/includes/sidebar";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";
import { userTentangMessages } from "../../../types/massage";

const DashboardProfile = () => {
  const [profiles, setProfiles] = useState<ProfileContent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [editingProfile, setEditingProfile] = useState<ProfileContent | null>(null);
  const [newProfile, setNewProfile] = useState<CreateProfileData>({ description: "" });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const response = await profileService.getProfile();
      setProfiles(Array.isArray(response) ? response : []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTentangMessages.LOAD_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    if (!newProfile.description.trim()) {
      setError(userTentangMessages.EMPTY_DESCRIPTION);
      setIsErrorModalOpen(true);
      return;
    }

    try {
      await profileService.createProfile(newProfile);
      await fetchProfiles();
      setSuccessMessage(userTentangMessages.CREATE_SUCCESS);
      setIsSuccessModalOpen(true);
      setNewProfile({ description: "" });
      setIsModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTentangMessages.CREATE_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    }
  };

  const handleUpdateProfile = async () => {
    if (!editingProfile) return;

    try {
      const updatedData: UpdateProfileData = {
        id: editingProfile.id,
        description: editingProfile.description,
      };
      await profileService.updateProfile(updatedData);
      await fetchProfiles();
      setSuccessMessage(userTentangMessages.UPDATE_SUCCESS);
      setIsSuccessModalOpen(true);
      setEditingProfile(null);
      setIsModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTentangMessages.UPDATE_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    }
  };

  const handleDeleteProfile = async (profileId: number) => {
    if (!window.confirm("Are you sure you want to delete this profile content?")) return;

    try {
      await profileService.deleteProfile(profileId);
      await fetchProfiles();
      setSuccessMessage(userTentangMessages.DELETE_SUCCESS);
      setIsSuccessModalOpen(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTentangMessages.DELETE_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    }
  };

  const truncateDescription = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const filteredProfiles = profiles.filter((profile) =>
    profile.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar  />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header - Mirip Gambar */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">👤</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Profile Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your company profile content</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingProfile(null);
                  setNewProfile({ description: "" });
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Content
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search profile content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">DESCRIPTION</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-40">CREATED AT</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProfiles.map((profile) => (
                    <tr key={profile.id} className="hover:bg-gray-50 transition-colors">
                      {/* ID Circle */}
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {profile.id}
                        </div>
                      </td>

                      {/* Description */}
                      <td className="px-8 py-6">
                        <div className="text-gray-700 leading-relaxed">
                          {truncateDescription(profile.description)}
                        </div>
                        {profile.description.length > 150 && (
                          <button className="text-blue-500 hover:underline text-sm mt-2 flex items-center gap-1">
                            <span>Read more</span>
                          </button>
                        )}
                      </td>

                      {/* Created At */}
                      <td className="px-8 py-6 text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(profile.created_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-4">
                          <button
                            onClick={() => {
                              setEditingProfile(profile);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteProfile(profile.id)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredProfiles.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-20 text-center text-gray-400">
                        No profile content found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Create/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">
              {editingProfile ? "Edit Profile Content" : "Add New Content"}
            </h2>

            <textarea
              value={editingProfile ? editingProfile.description : newProfile.description}
              onChange={(e) => {
                if (editingProfile) {
                  setEditingProfile({ ...editingProfile, description: e.target.value });
                } else {
                  setNewProfile({ description: e.target.value });
                }
              }}
              placeholder="Enter your profile description here..."
              className="w-full border border-gray-300 rounded-2xl px-6 py-4 h-80 resize-y focus:outline-none focus:border-purple-500 text-base"
            />

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={editingProfile ? handleUpdateProfile : handleCreateProfile}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium transition-all"
              >
                {editingProfile ? "Update Content" : "Create Content"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Success</h2>
            <p className="text-gray-700 mb-6">{successMessage}</p>
            <button
              onClick={() => setIsSuccessModalOpen(false)}
              className="bg-green-500 text-white px-8 py-3 rounded-2xl hover:bg-green-600"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {isErrorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => setIsErrorModalOpen(false)}
              className="bg-red-500 text-white px-8 py-3 rounded-2xl hover:bg-red-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardProfile;